import { Button, Input } from '@artosera/ui';
import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { authApi } from '../../api/auth.js';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import { useApresConnexion } from '../../auth/session.js';
import { AuthCard } from '../AuthCard.js';
import { Field } from '../Field.js';
import { allerApresConnexion, useContexteConnexion } from './connexionFlow.js';
import styles from './ConnexionPage.module.css';
import { retourSur } from '../../auth/retour.js';

/** `/connexion` : e-mail et mot de passe, erreurs en français depuis l'API. */
export function ConnexionPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { poserIdentifiantsEnAttente } = useContexteConnexion();
  const apresConnexion = useApresConnexion();

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // Jamais une adresse d'un autre site (`auth/retour.ts`).
  const retour = retourSur(searchParams.get('retour'));

  async function soumettre(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await authApi.connexion({ email, motDePasse });
      if (reponse.deuxiemeFacteurRequis) {
        poserIdentifiantsEnAttente({ email, motDePasse });
        navigate(`/connexion/2fa?retour=${encodeURIComponent(retour)}`);
        return;
      }
      await apresConnexion();
      await allerApresConnexion(navigate, retour);
    } catch (error) {
      if (estApiError(error)) {
        setErreur(error.message);
      } else if (estErreurReseau(error)) {
        setErreur(t('auth.connexion.erreurReseau'));
      } else {
        setErreur(t('commun.erreurReseau'));
      }
    } finally {
      setEnCours(false);
    }
  }

  // Liens de réinitialisation émis avant le 3 octobre 2026 : l'API pointait
  // `/connexion?reinitialisation=<jeton>`, que cette page ignorait. On les
  // renvoie vers la page qui sait les traiter, le temps qu'ils expirent.
  const jetonAncien = searchParams.get('reinitialisation');
  if (jetonAncien) {
    return (
      <Navigate to={`/mot-de-passe/reinitialiser/${encodeURIComponent(jetonAncien)}`} replace />
    );
  }

  return (
    <AuthCard title={t('auth.connexion.titre')} subtitle={t('auth.connexion.sousTitre')}>
      <form onSubmit={(event) => void soumettre(event)} noValidate>
        <Field label={t('auth.connexion.champEmail')} htmlFor="connexion-email">
          <Input
            id="connexion-email"
            type="email"
            autoComplete="email"
            required
            className={styles.pleineLargeur}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label={t('auth.connexion.champMotDePasse')} htmlFor="connexion-mot-de-passe">
          <Input
            id="connexion-mot-de-passe"
            type="password"
            autoComplete="current-password"
            required
            className={styles.pleineLargeur}
            value={motDePasse}
            onChange={(event) => setMotDePasse(event.target.value)}
          />
        </Field>

        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}

        <Button type="submit" className={styles.envoi} loading={enCours}>
          {t('auth.connexion.bouton')}
        </Button>
      </form>

      <p className={styles.lienOubli}>
        <Link to={`/connexion/sso?retour=${encodeURIComponent(retour)}`}>{t('auth.sso.lien')}</Link>
      </p>
      <p className={styles.lienOubli}>
        <Link to="/mot-de-passe/oublie">{t('auth.connexion.motDePasseOublie')}</Link>
      </p>
    </AuthCard>
  );
}
